/** Tile colors. Order is also the refill bag order. */
export const TILE_COLORS = [
  'acorn',
  'berry',
  'leaf',
  'blossom',
  'honey',
  'moonstone',
] as const;

export type TileColor = (typeof TILE_COLORS)[number];

/**
 * Specials created by matches:
 * - line-row: 4-in-a-row horizontal. Clears its row when triggered.
 * - line-col: 4-in-a-column vertical. Clears its column when triggered.
 * - bomb: L or T group. Clears a 3×3 when triggered.
 * - rainbow: straight line of 5+. Clears a color when triggered.
 * A tile holds at most one special.
 */
export type Special = 'line-row' | 'line-col' | 'bomb' | 'rainbow';

/**
 * Statuses sit on a tile until they expire.
 * `duration` is how many later turn-ends of `owner` it survives.
 * The turn it is planted does not count. `duration` 0 resolves immediately.
 */
export type StatusKind = 'locked' | 'decoy' | 'mark' | 'shield' | 'buried-bomb' | 'seed';

export interface TileStatus {
  kind: StatusKind;
  duration: number;
  owner: PlayerId;
  /**
   * Owner's `turnsTaken` after the planting turn.
   * The turn-end that reaches this number does not tick the status.
   */
  placedTurn: number;
  /**
   * Decoy: painted color. Matching and meters use `Tile.color`.
   * When the other player clears the tile, its points go to `owner`.
   */
  disguise?: TileColor;
  /** Mark: extra points for `owner` when this tile is cleared. */
  bonus?: number;
  /** Seed: special written onto the tile when the seed resolves. */
  grows?: Special;
  /**
   * Buried bomb: squares cleared around the tile. 1 is a 3×3, 2 is a 5×5.
   * Omitted means 1.
   */
  radius?: number;
}

/**
 * Venue pieces painted on an ordinary tile.
 * Absent on every match that is not standing in a venue, so the hash stays put.
 * `coated` is a honey jar of `tile.color`: it matches as that color.
 * `ice` matches as its color and shatters into a cross. `wild` matches as any color.
 * `lantern` keeps its color and matches as that color.
 * `puddle` matches as its own icon. A match that includes one washes that color.
 */
export type TileRole = 'coated' | 'mushroom' | 'dandelion' | 'wild' | 'lantern' | 'ice' | 'puddle';

export interface Tile {
  id: number;
  color: TileColor;
  special: Special | null;
  statuses?: TileStatus[];
  /** Set by a venue. Omitted on a plain tile. */
  role?: TileRole;
}

export type Cell = Tile | null;

export interface Position {
  r: number;
  c: number;
}

export type PlayerId = 0 | 1;

export type HeroId =
  | 'pip'
  | 'bruno'
  | 'tuck'
  | 'bramble'
  | 'thistle'
  | 'marlow'
  | 'cleo'
  | 'wren'
  | 'juniper'
  | 'otto'
  | 'lumi'
  | 'barley'
  | 'fern'
  | 'posy'
  | 'dash';

export type Phase = 'playing' | 'sudden-death' | 'ended';

/**
 * One equipped trick. `charge` fills toward the catalog cost on a legacy match.
 * A Charm duel ignores charge: the trick fires once, then `spent` is set.
 */
export interface EquippedPowerup {
  id: string;
  charge: number;
  spent?: boolean;
}

export interface PlayerState {
  id: PlayerId;
  heroId: HeroId;
  score: number;
  /** Current meter charge, capped at config.meterMax. */
  meter: number;
  movesLeft: number;
  turnsTaken: number;
  /** Hostile powerups this player can still shrug off. */
  shields: number;
  /** At most two equipped tricks. Empty when the match was not given a loadout. */
  loadout: EquippedPowerup[];
  /** Charm staked on this duel. Absent on a legacy signature match. */
  charmId?: string;
  /** Hornbeam Shield: the next rival Charm fizzles. Absent when no ward is up. */
  charmWard?: boolean;
  /** Set by Hearthstone. Absent when no refund is waiting. */
  hearth?: HearthWard;
  /**
   * Moves Snail Mail borrowed. The next turn this player starts is shorter
   * by this many, then the debt clears. Capped at 2 so a turn still has 3 moves.
   */
  mailOwed?: number;
  /**
   * Points the rival has scored against this seat since their last turn.
   * Spent as a small meter bonus when the seat plays again. Omitted at 0
   * so a fresh match still hashes the same.
   */
  comeback?: number;
}

/** Rule values copied onto a match so a 9×9 (or masked) game cannot drift from the constant. */
export interface MatchConfig {
  boardW: number;
  boardH: number;
  movesPerTurn: number;
  maxBonusMovesPerTurn: number;
  turnsPerPlayer: number;
  meterMax: number;
  meterPerTile: number;
  pointsPerTile: number;
  /** Seconds one player has for every move of their turn, including bonus moves. */
  turnSeconds: number;
  /**
   * Set on a non-standard match (`quick`, `five-by-two`, `six-by-three`).
   * Omitted on the default 4×5 duel so that game still hashes the same.
   */
  format?: string;
  /**
   * Charge cap for a trick id. Missing ids use the catalog cost.
   * Only a format that retunes costs sets this.
   */
  trickCosts?: Readonly<Record<string, number>>;
  /**
   * Per-seat charge caps. A missing seat uses trickCosts, then the catalog.
   * Omitted entirely on matches that have no seat discount, so the hash stays put.
   */
  seatCosts?: readonly [
    Readonly<Record<string, number>> | null,
    Readonly<Record<string, number>> | null,
  ];
  /**
   * Charm duels: the meter is the Charm, tricks fire once and spend no move.
   * Omitted on the legacy signature match so its hash stays put.
   */
  ruleset?: 'charm';
}

export interface MatchState {
  /** Seed passed to createMatch. Replay starts here. */
  seed: number;
  /** Current mulberry32 state. Refills, reshuffles, and hero RNG advance this. */
  rngState: number;
  config: MatchConfig;
  /**
   * Playable cells. `null` means the full rectangle is playable.
   * Inactive cells stay empty; tiles fall through them.
   */
  mask: boolean[][] | null;
  /** Row 0 is the top. `null` is an empty hole. */
  board: Cell[][];
  nextTileId: number;
  players: [PlayerState, PlayerState];
  currentPlayer: PlayerId;
  phase: Phase;
  winner: PlayerId | 'tie' | null;
  bonusMovesGrantedThisTurn: number;
  /**
   * The latest match this seat cleared. Hollow Echo reads it.
   * Absent until the first match, so an unplayed board hashes as before.
   */
  lastClear?: { player: PlayerId; color: TileColor; size: number };
  /**
   * Trophy-road venue. Omitted on every match that has no venue, so those
   * games still hash the same.
   */
  venue?: VenueRuntime;
}

/** One critter walking the board. `dir` is E S W N for a firefly. Ants and snails leave it at 0. */
export interface VenueActor {
  kind: 'snail' | 'frog' | 'firefly' | 'ant' | 'caterpillar';
  r: number;
  c: number;
  dir: number;
}

/**
 * Live venue state. `scoreMul` stays 1. `path` stays empty; the old firefly trail is gone.
 */
export interface VenueRuntime {
  id: string;
  scoreMul: number;
  path: string[];
  actors: VenueActor[];
  /** Kept so a venue hash stays the same shape. Rainbow Puddle no longer cycles it. */
  hint: number;
  /** Ants matched, seat 0 then seat 1. */
  treats: [number, number];
  /** A venue asked spendMove for one bonus. Cleared once it is paid. */
  bonusArmed?: boolean;
  /** Pillow bursts this turn. Stops a chain from walking the whole board. */
  bursts: number;
}

export interface SwapMove {
  type: 'swap';
  from: Position;
  to: Position;
}

/** Which way Bramble's tunnel runs. Absent means a column, so older aims still burrow. */
export type LineAxis = 'row' | 'column';

/** Which edge Otto's wave travels toward. Absent means down, the bottom rows. */
export type WaveDir = 'up' | 'down' | 'left' | 'right';

export interface UsePowerMove {
  type: 'usePower';
  player: PlayerId;
  /**
   * Pip reads `r` as the row and `c` as the tile he rolls into.
   * Bramble reads `r` or `c` depending on `axis`.
   * Cleo reads both as the center of the bloom.
   */
  target?: Position;
  /** Bramble's tunnel. Omitted means column. */
  axis?: LineAxis;
  /** Otto's wave. Omitted means down. */
  dir?: WaveDir;
  /** Wish Willow. Three tiles, omitted on every other Charm. */
  picks?: Position[];
}

export interface UsePowerupMove {
  type: 'usePowerup';
  player: PlayerId;
  /** Index into that player's loadout. */
  slot: 0 | 1;
  /** Tile, row, and column powers read this. Pair powers read this as the first tile. */
  target?: Position;
  /** Second tile for a pair power. */
  alt?: Position;
  /** Color powers read this. */
  color?: TileColor;
}

/** Detonate a stripe or a bee where it sits. Rainbow still needs a swap. */
export interface ActivateMove {
  type: 'activate';
  at: Position;
}

export type Move = SwapMove | UsePowerMove | UsePowerupMove | ActivateMove;

export type RejectReason =
  | 'game-over'
  | 'not-adjacent'
  | 'out-of-bounds'
  | 'empty'
  | 'no-match'
  | 'wrong-player'
  | 'meter-not-full'
  | 'missing-target'
  | 'powerup-not-ready'
  | 'unknown-powerup'
  | 'locked';

/** How a powerup asks the player to aim. `none` fires on the tap. `pair` wants two tiles. */
export type PowerupTarget = 'none' | 'tile' | 'row' | 'column' | 'color' | 'pair';

/** Which cells the aim preview lights. Tile powers pick a shape; the others match the target. */
export type PowerupAim =
  | 'none'
  | 'cell'
  | 'plus'
  | 'square'
  | 'row'
  | 'column'
  | 'color'
  | 'x'
  | 'double-row'
  | 'trail'
  | 'ring';

/**
 * Hearthstone's pity ward. The turn after the one that cast it is watched.
 * `turnsTaken` during that turn equals `watchTurn`. When the turn ends and
 * fewer than `under` tiles were cleared, `refund` charge returns to `slot`.
 */
export interface HearthWard {
  slot: number;
  refund: number;
  under: number;
  watchTurn: number;
  clears: number;
}

export type MatchShape = 'plain' | 'line-row' | 'line-col' | 'bomb' | 'rainbow';

export interface SwapEvent {
  type: 'swap';
  from: Position;
  to: Position;
  /** Tile that started on `from` (it lands on `to`). */
  tileIdA: number;
  /** Tile that started on `to`. */
  tileIdB: number;
}

export interface MatchEvent {
  type: 'match';
  /** 0 is the swap's own match. Later waves are cascades. */
  cascade: number;
  color: TileColor;
  cells: Position[];
  size: number;
  shape: MatchShape;
}

export interface SpecialCreatedEvent {
  type: 'special-created';
  cascade: number;
  position: Position;
  tileId: number;
  special: Special;
  /** Stripes and bombs match as this color. A rainbow keeps it but does not join runs. */
  color: TileColor;
}

export interface SpecialTriggeredEvent {
  type: 'special-triggered';
  cascade: number;
  position: Position;
  tileId: number;
  special: Special;
  clears: Position[];
}

export interface ClearCell {
  position: Position;
  tileId: number;
  color: TileColor;
  special: Special | null;
}

export interface ClearEvent {
  type: 'clear';
  cascade: number;
  cells: ClearCell[];
  scoreDelta: number;
  player: PlayerId;
}

export interface GravityMove {
  tileId: number;
  from: Position;
  to: Position;
}

export interface GravityEvent {
  type: 'gravity';
  cascade: number;
  moves: GravityMove[];
}

export interface RefillTile {
  tileId: number;
  position: Position;
  color: TileColor;
  special: null;
}

export interface RefillEvent {
  type: 'refill';
  cascade: number;
  tiles: RefillTile[];
}

export interface CascadeEvent {
  type: 'cascade';
  /** 1-based extra wave. Multiplier is index + 1, matching score math. */
  index: number;
  multiplier: number;
}

export interface ScoreEvent {
  type: 'score';
  player: PlayerId;
  delta: number;
  total: number;
  cascade: number;
}

export interface MeterEvent {
  type: 'meter';
  player: PlayerId;
  delta: number;
  total: number;
  color: TileColor;
}

export interface BonusMoveEvent {
  type: 'bonus-move';
  player: PlayerId;
  movesLeft: number;
  bonusMovesGranted: number;
}

export interface TurnEndedEvent {
  type: 'turn-ended';
  player: PlayerId;
  nextPlayer: PlayerId;
  turnsTaken: number;
}

/** The clock ran out. Unused moves, including a bonus move, are discarded. */
export interface TurnTimeoutEvent {
  type: 'turn-timeout';
  player: PlayerId;
  movesLost: number;
}

export interface SuddenDeathEvent {
  type: 'sudden-death';
}

export interface MatchEndedEvent {
  type: 'match-ended';
  winner: PlayerId | 'tie';
  scores: [number, number];
}

export interface ReshuffleEvent {
  type: 'reshuffle';
  moves: GravityMove[];
}

export interface PowerEvent {
  type: 'power';
  player: PlayerId;
  heroId: HeroId;
  powerName: string;
  /** Charm that fired, when the match is using Charms. */
  charmId?: string;
  /**
   * Cells the power itself clears or converts, before special chains and cascades.
   * The renderer highlights these, then plays the following clear or convert events.
   */
  cells: Position[];
  target?: Position;
  /** Bramble's tunnel, when the cast chose a row or a column. */
  axis?: LineAxis;
  /** Otto's wave, when the cast aimed an edge. */
  dir?: WaveDir;
  /** Hornbeam Shield swallowed this Charm. */
  blocked?: boolean;
}

export interface ConvertCell {
  position: Position;
  tileId: number;
  from: TileColor;
  to: TileColor;
}

export interface ConvertEvent {
  type: 'convert';
  cells: ConvertCell[];
}

export interface MoveRejectedEvent {
  type: 'move-rejected';
  reason: RejectReason;
}

export interface PowerupEvent {
  type: 'powerup';
  player: PlayerId;
  id: string;
  name: string;
  slot: 0 | 1;
  targetMode: PowerupTarget;
  cells: Position[];
  target?: Position;
  /** Second tile, when the trick swaps a pair. */
  alt?: Position;
  color?: TileColor;
  /** A shield swallowed the effect. Charge is still spent. */
  blocked?: boolean;
}

export interface PowerupChargeEvent {
  type: 'powerup-charge';
  player: PlayerId;
  slot: number;
  id: string;
  delta: number;
  total: number;
  cost: number;
}

export interface StatusEvent {
  type: 'status';
  position: Position;
  tileId: number;
  kind: StatusKind;
  duration: number;
  owner: PlayerId;
  change: 'added' | 'removed' | 'resolved';
}

export interface MovesGrantedEvent {
  type: 'moves-granted';
  player: PlayerId;
  delta: number;
  movesLeft: number;
}

/** Hearthstone paid its refund. `amount` is what fit under the catalog cost. */
export interface HearthRefundEvent {
  type: 'hearth-refund';
  player: PlayerId;
  slot: number;
  amount: number;
}

/**
 * One step of a matched frog's payoff.
 * `begin` carries the whole plan. Each `land` is one hop and the cells
 * that hop actually removes. `exit` is the frog leaving.
 * A landing clears the landing cell and its orthogonal neighbors, clipped
 * to the board, and nothing past that plus.
 */
export interface FrogSplash {
  r: number;
  c: number;
  /** Match size that chose the hop count. 5+ still hops five times. */
  size: number;
  phase: 'begin' | 'land' | 'exit';
  landings: Position[];
  pluses: Position[][];
}

/**
 * One matched snail's run. It faces left, the way the painting looks.
 * `passed` is the home-row cells in front of it.
 * `row` is the other row it clears, and stays empty when every row already
 * has a snail. `hopRow` is that row, or null when the second clear is skipped.
 */
/** One matched caterpillar. `ring` is the outer 5×5, clipped to the board. */
export interface CocoonRing {
  r: number;
  c: number;
  ring: Position[];
}

export interface SnailDash {
  r: number;
  c: number;
  face: -1 | 1;
  hopRow: number | null;
  passed: Position[];
  row: Position[];
}

/**
 * One matched ant's march. `path` starts on the ant and visits adjacent
 * cells without repeating. The line carries off every tile on it.
 */
export interface AntMarch {
  r: number;
  c: number;
  path: Position[];
  /** Match size that chose the path length. */
  size: number;
  /** The icon this ant was carrying, which is the color the match used. */
  color: TileColor;
}

/**
 * One rainbow wash. `r,c` is the puddle the ripple starts from.
 * `cells` is every tile of that color on the board when the match landed.
 */
export interface PuddleWash {
  color: TileColor;
  r: number;
  c: number;
  cells: Position[];
}

/**
 * One matched mushroom's bounce. `from` is the pad it springs off.
 * `to` is the seeded landing, at least two tiles away, where the whole 3×3 fits.
 */
export interface MushroomHop {
  from: Position;
  to: Position;
  color: TileColor;
}

/** A venue rule the bot can price, and the board can show. */
export interface VenueEvent {
  type: 'venue';
  kind: 'reshuffle' | 'sneeze' | 'treat' | 'bounce' | 'burst' | 'trail' | 'splash' | 'spark' | 'march' | 'wash' | 'cocoon';
  player: PlayerId;
  /** Set when `kind` is `splash`. */
  splash?: FrogSplash;
  /** Set when `kind` is `trail`: each matched snail's two-row clear. */
  dashes?: SnailDash[];
  /** Set when `kind` is `spark`: the lantern, then each tile the spark reaches. */
  spark?: Position[];
  /** Set when `kind` is `march`: each matched ant's path. */
  marches?: AntMarch[];
  /** Set when `kind` is `wash`: one ripple per matched puddle color. */
  washes?: PuddleWash[];
  /** Set when `kind` is `cocoon`: each matched caterpillar's silk ring. */
  rings?: CocoonRing[];
  /** Set when `kind` is `bounce`: each matched mushroom's two landings. */
  hops?: MushroomHop[];
}

export type GameEvent =
  | SwapEvent
  | MatchEvent
  | SpecialCreatedEvent
  | SpecialTriggeredEvent
  | ClearEvent
  | GravityEvent
  | RefillEvent
  | CascadeEvent
  | ScoreEvent
  | MeterEvent
  | BonusMoveEvent
  | TurnTimeoutEvent
  | TurnEndedEvent
  | SuddenDeathEvent
  | MatchEndedEvent
  | ReshuffleEvent
  | PowerEvent
  | ConvertEvent
  | MoveRejectedEvent
  | PowerupEvent
  | PowerupChargeEvent
  | StatusEvent
  | MovesGrantedEvent
  | HearthRefundEvent
  | VenueEvent;

export interface ApplyResult {
  state: MatchState;
  events: GameEvent[];
}

/** Returned by a hero. The engine applies it, then gravity, refill, and cascades. */
export interface HeroEffect {
  clear: Position[];
  convert: Array<{ position: Position; color: TileColor }>;
}

/**
 * How a signature asks to be aimed.
 * `row` clears that row. `line` is a row or a column. `area` is a center.
 * `wave` is an edge. `none` fires with no aim.
 */
export type HeroTarget = 'row' | 'column' | 'line' | 'area' | 'wave' | 'none';

/** Playable classes. Grove is Fern, Posy, and Dash. */
export type HeroClassId = 'guardian' | 'infiltration' | 'stealth' | 'tidecaller' | 'grove';

export interface HeroDefinition {
  id: HeroId;
  name: string;
  species: string;
  /** Which class catalog this critter draws powerups from. */
  classId: HeroClassId;
  powerName: string;
  color: TileColor;
  /** Signature power, shown on the powerup page. The class line lives with the class. */
  description: string;
  target: HeroTarget;
  /**
   * Back-of-paw sprite under `public/assets/heroes/`.
   * Toes point up-left; every hero, including ones added later, defines one.
   */
  paw: string;
  /**
   * May advance `state.rngState`. Must not change the board.
   * `state` is already a working copy owned by `applyMove`.
   * `aim` carries a tunnel axis or a wave direction when the hero uses one.
   */
  apply(
    state: MatchState,
    player: PlayerId,
    target?: Position,
    aim?: { axis?: LineAxis; dir?: WaveDir },
  ): HeroEffect;
}
